import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Params } from '@angular/router';
import { SHARED_MODULES } from '@app/shared';
import { RequiredAccessArray } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { first, flattenDeep, isNil } from 'lodash';
import { combineLatest, Observable, of, take } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { SHORTCUT_BUILDERS, ShortcutBuilder } from './shortcut';
import { ShortcutCoordinator } from './shortcut-coordinator';
import { ShortcutStudent } from './shortcut-student';
import { ShortcutTeacher } from './shortcut-teacher';

interface Shortcut {
  title: string;
  icon: string;
  style: string;
  iconStyle?: string;
  permission?: RequiredAccessArray;
  show?: Observable<boolean>;
  url?: string;
  params?: Params;
  open?: () => Observable<any>;
  redirect?: string;
  load?: WritableSignal<boolean>;
}
@Component({
  selector: 'app-shortcut-list',
  imports: [SHARED_MODULES],
  templateUrl: './shortcut-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: SHORTCUT_BUILDERS, useExisting: ShortcutStudent, multi: true },
    { provide: SHORTCUT_BUILDERS, useExisting: ShortcutTeacher, multi: true },
    { provide: SHORTCUT_BUILDERS, useExisting: ShortcutCoordinator, multi: true },
  ],
})
export class ShortcutList implements OnInit {
  protected items$: Observable<any[]>;
  private readonly destroy = inject(DestroyRef);
  private readonly store = inject<Store<AppState>>(Store);
  private readonly shortcuts = inject<ShortcutBuilder[]>(SHORTCUT_BUILDERS);

  public ngOnInit() {
    this.load();
  }

  public onOpen(item: Shortcut) {
    if (!item.load()) {
      item.load.set(true);
      item
        .open()
        .pipe(take(1))
        .subscribe(() => item.load.set(false));
    }
  }

  public routerLink(item): string {
    return first(flattenDeep([item.url]));
  }

  private load() {
    this.items$ = this.store.select(AppSelectors.appFeature).pipe(
      takeUntilDestroyed(this.destroy),
      map((store) => this.shortcuts.find(({ accept }) => accept(store))?.items || []),
      switchMap((data) => combineLatest(data.map((item) => this.resolve(item())))),
      map((items) => items.filter(({ show, permission }) => show && permission)),
    );
  }

  private resolve(item: Shortcut): Observable<any> {
    return combineLatest({
      show: this.helperShow(item.show),
      permission: this.helperAllow(item.permission),
      item: of(item),
    }).pipe(
      take(1),
      map(({ show, permission, item }) => ({ ...item, show, permission })),
    );
  }

  private helperShow(show: Observable<boolean>): Observable<boolean> {
    if (isNil(show)) {
      return of(true);
    }
    return show;
  }

  private helperAllow(permission: RequiredAccessArray): Observable<boolean> {
    if (isNil(permission)) {
      return of(true);
    }
    return this.store.select(AppSelectors.isAllow(permission));
  }
}
